import { Component, EventEmitter, Input, Output } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { Floorplan, ProductTypesInGroup } from "@domain/project/floorplan/floorplan";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { getFloorplanItems } from "@domain/project/floorplan/floorplan-item.utils";
import { FloorplanPlaceholderType, FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import type { ScopedProject } from "@domain/project/project-guards";
import {
  CheckCellComponent,
  TableComponent,
  TableHeaderCell,
  TableModule,
  TableRowDirective,
} from "@odx/angular/components/table";

interface TableData {
  selected: boolean;
  id: string;
  name: string;
  productId: string;
  displayName: string;
  floorplanName: string;
  notes: string;
  product: ProductTypesInGroup;
}
@Component({
  selector: "app-group-configuration-table",
  standalone: true,
  imports: [TableComponent, CheckCellComponent, UiKitModule, TableRowDirective, TableModule],
  templateUrl: "./group-configuration-table.component.html",
  styleUrl: "./group-configuration-table.component.scss",
})
export class GroupConfigurationTableComponent {
  @Input() group!: ProductItemGroup;
  @Input() floorplan!: Floorplan;
  @Input() project!: ScopedProject;
  @Input() set productsToDisplay(products: ProductTypesInGroup[]) {
    this.products = products;
    this.dataToDisplay = this.getData(products);
  }

  @Output() selectedProductsChanged = new EventEmitter<ProductTypesInGroup[]>();

  protected dataToDisplay: TableData[] = [];
  private filterParams!: Record<string, string>;
  protected products!: ProductTypesInGroup[];

  protected headerData: TableHeaderCell[] = [
    { name: "selected", check: true },
    { name: "id", title: $localize`:@@groupConfiguration.table.column0.header:Element-ID`, filter: true },
    { name: "name", title: $localize`:@@groupConfiguration.table.column1.header:Bezeichnung`, filter: true },
    { name: "productId", title: $localize`:@@groupConfiguration.table.column2.header:Sachnummer`, filter: true },
    {
      name: "displayName",
      title: $localize`:@@groupConfiguration.table.column3.header:Name auf dem Grundrissplan`,
      filter: true,
    },
    {
      name: "floorplanName",
      title: $localize`:@@groupConfiguration.table.column5.header:Grundrissplan`,
      filter: true,
    },
    { name: "notes", title: $localize`:@@groupConfiguration.table.column4.header:Notizen`, filter: true },
    { name: "product", title: "", sortable: false, filter: false },
  ];

  private getData(products: ProductTypesInGroup[]) {
    const tableData: TableData[] = [];
    products.forEach((product) => {
      tableData.push(this.productToTableData(product));
    });
    const filteredData = this.filterData(tableData);

    return filteredData;
  }

  private productToTableData(product: ProductTypesInGroup) {
    return {
      selected: false,
      id: product.positionId ?? "",
      name: this.setProductName(product),
      productId: this.setProductId(product),
      displayName: product.displayName ? product.displayName : "",
      floorplanName: this.resolveFloorplanName(product),
      notes: product.notes ? product.notes : "",
      product: product,
    };
  }

  private resolveFloorplanName(product: ProductTypesInGroup): string {
    const ref = this.group.references.find((r) => r.floorplanProductItemId === product.id);
    if (ref) {
      return this.project?.getFloorplanById(ref.floorplanId)?.name ?? "";
    }
    const floorplans: Floorplan[] = this.project?.cachedFloorplans ?? [this.floorplan];
    return floorplans.find((fp) => getFloorplanItems(fp).some((item) => item.id === product.id))?.name ?? "";
  }

  private setProductId(product: ProductTypesInGroup) {
    if (product instanceof FloorplanAlarmDevice) return product.config.productIds[0];

    if (product instanceof FloorplanPatientMonitor) return product.config.familyNumber;

    if (product instanceof FloorplanCentralStation) return product.config.familyNumber;

    if (product instanceof FloorplanTelemetry) return product.config.familyNumber;

    if (product instanceof FloorplanKvm) return product.config.familyNumber;

    if (product instanceof FloorplanUtility) return product.config.familyNumber;

    return "config" in product && "productId" in product.config ? product.config.productId : "";
  }

  private setProductName(product: ProductTypesInGroup) {
    if (product instanceof FloorplanSimplePlaceholder) {
      if (product.type === FloorplanPlaceholderType.ALARM_DEVICE)
        return $localize`:@@floorplan.details.placeholder.alarmDevice.header:Platzhalter (Alarmmittel)`;
      if (product.type === FloorplanPlaceholderType.PLASTIC_SIGN)
        return $localize`:@@floorplan.details.placeholder.plasticSign.header:Platzhalter (Kunststoffschild)`;
      if (product.type === FloorplanPlaceholderType.SIGNAL_ELEMENT)
        return $localize`:@@floorplan.details.placeholder.signalElement.header:Platzhalter (Leuchttransparent)`;
      if (product.type === FloorplanPlaceholderType.PATIENT_MONITOR)
        return $localize`:@@floorplan.details.placeholder.patientMonitor.header:Placeholder (Patient Monitor)`;
      if (product.type === FloorplanPlaceholderType.TELEMETRY)
        return $localize`:@@floorplan.details.placeholder.telemetry.header:Placeholder (Telemetry)`;
      if (product.type === FloorplanPlaceholderType.KVM)
        return $localize`:@@floorplan.details.placeholder.kvm.header:Placeholder (KVM)`;
      if (product.type === FloorplanPlaceholderType.DISPLAY)
        return $localize`:@@floorplan.details.placeholder.display.header:Placeholder (Display)`;
      if (product.type === FloorplanPlaceholderType.UTILITY)
        return $localize`:@@floorplan.details.placeholder.utility.header:Placeholder (Utility)`;
    }
    if (product instanceof FloorplanGasWarningCenterPlaceholder)
      return $localize`:@@floorplan.details.placeholder.gasWarningCenter.header:Platzhalter (Gaswarnzentrale)`;
    if (product instanceof FloorplanTransmitterPlaceholder)
      return $localize`:@@floorplan.details.placeholder.transmitter.header:Platzhalter (Transmitter)`;
    if (product instanceof FloorplanFlameDetectorPlaceholder)
      return $localize`:@@floorplan.details.placeholder.flameDetector.header:Placeholder (Flame Detector)`;

    return "config" in product && "name" in product.config ? product.config.name : "";
  }

  checkAll($event: { column: string; check: boolean }) {
    const { column, check } = $event;
    this.dataToDisplay.forEach((item) => {
      if (column in item) (item[column as keyof TableData] as boolean) = check;
    });
  }

  filter(filterParams: Record<string, string>) {
    this.filterParams = filterParams;
    this.dataToDisplay = this.getData(this.products);
  }

  onSelectionChanged() {
    this.selectedProductsChanged.emit(this.selectedProducts);
  }

  public deselectAll() {
    this.dataToDisplay.map((product) => (product.selected = false));
  }

  private filterData(data: TableData[]): TableData[] {
    if (!this.filterParams) return data;

    const filteredData = data.filter((item: TableData) => {
      return Object.keys(this.filterParams)
        .map(
          (key) =>
            key in item &&
            item[key as keyof TableData].toString().toLowerCase().indexOf(this.filterParams[key]?.toLowerCase().trim()) !== -1,
        )
        .every(Boolean);
    });

    return filteredData;
  }

  get selectedProducts() {
    return this.dataToDisplay.filter((product) => product.selected).map((product) => product.product);
  }
}
